import { apiRequest, apiRequestNoContent } from '@lib/api';

import {
    FeatureFlagDefault,
    FlagDefinition,
    FlagDetail,
    FlagListResponse,
    TenantFlagOverride,
    type FlagEnvironment,
    type FlagLifecycle,
    type FlagListItem,
} from '../schemas/flag';

/**
 * The flags API, one function per route.
 *
 * This replaces `mock-flags.ts`. The mock's promise — "swapping to a real client
 * is a one-file change" — held on the transport but not on the shapes: the API
 * returns `{ definition, default }` pairs rather than the flattened rows the
 * mock invented, so the components moved with it. See `schemas/flag.ts`.
 */

const encodeKey = (key: string) => encodeURIComponent(key);

/* ------------------------------------------------------------------ reads - */

export async function listFlags(signal?: AbortSignal): Promise<FlagListItem[]> {
    const { flags } = await apiRequest('/flags', FlagListResponse, { signal });
    return flags;
}

export function getFlag(key: string, signal?: AbortSignal): Promise<FlagDetail> {
    return apiRequest(`/flags/${encodeKey(key)}`, FlagDetail, { signal });
}

/* ----------------------------------------------------------------- writes - */

export interface CreateFlagInput {
    readonly key: string;
    readonly name: string;
    readonly description: string;
    readonly category: string;
    readonly lifecycle: FlagLifecycle;
    readonly owner: string;
    readonly reason?: string;
}

/** Create a flag. It is always created **off**, so this can never turn behaviour on. */
export function createFlag(input: CreateFlagInput): Promise<FlagDefinition> {
    return apiRequest('/flags', FlagDefinition, { method: 'POST', body: input });
}

export interface SetDefaultInput {
    readonly enabled: boolean;
    /**
     * Omitting this **clears** an existing rollout gate — it is not "leave it
     * alone". Callers must round-trip whatever they read, or a toggle silently
     * promotes a 25% rollout to 100% for every museum at once.
     */
    readonly rolloutPercentage?: number;
    /** Omitted keeps the current value, so flipping a flag on cannot un-archive it. */
    readonly archived?: boolean;
    readonly expectedVersion: number;
    readonly reason?: string;
}

/** The widest-blast-radius write in the app: every museum without an override. */
export function setDefault(key: string, input: SetDefaultInput): Promise<FeatureFlagDefault> {
    return apiRequest(`/flags/${encodeKey(key)}/default`, FeatureFlagDefault, {
        method: 'PUT',
        body: input,
    });
}

export interface SetEnvironmentDefaultInput {
    readonly enabled: boolean;
    /** Same round-trip rule as the platform default — see {@link SetDefaultInput}. */
    readonly rolloutPercentage?: number;
    readonly archived?: boolean;
    /**
     * The version the operator's view was rendered from, or `null` when that
     * view showed no row for this environment yet — this write is the first
     * one for this (key, environment) pair. Nullable, not optional: a caller
     * that forgot the field is a bug, while a caller sending `null`
     * deliberately is reporting a real state. See `setEnvironmentDefaultBody`
     * in `apps/api`.
     */
    readonly expectedVersion: number | null;
    readonly reason?: string;
}

/**
 * Set a flag's default in one backend environment's partition — the value that
 * environment resolves to for every tenant without an override, in preference
 * to the platform default for that environment only (ADR 0007 Option B).
 * Narrower blast radius than {@link setDefault}: every tenant in one
 * environment, not every tenant everywhere.
 */
export function setEnvironmentDefault(
    key: string,
    environment: FlagEnvironment,
    input: SetEnvironmentDefaultInput,
): Promise<FeatureFlagDefault> {
    return apiRequest(
        `/flags/${encodeKey(key)}/default/${encodeKey(environment)}`,
        FeatureFlagDefault,
        { method: 'PUT', body: input },
    );
}

/**
 * Stop overriding one environment; it falls back to the platform default for
 * this key. Idempotent — clearing an environment that already inherits is a
 * 204, not a 404, matching {@link clearOverride}.
 */
export function clearEnvironmentDefault(
    key: string,
    environment: FlagEnvironment,
    reason?: string,
): Promise<void> {
    return apiRequestNoContent(`/flags/${encodeKey(key)}/default/${encodeKey(environment)}`, {
        method: 'DELETE',
        ...(reason !== undefined ? { body: { reason } } : {}),
    });
}

export interface SetOverrideInput {
    readonly enabled: boolean;
    /** Same round-trip rule as the default — see {@link SetDefaultInput}. */
    readonly rolloutPercentage?: number;
    readonly reason?: string;
}

export function setOverride(
    key: string,
    tenantId: string,
    input: SetOverrideInput,
): Promise<TenantFlagOverride> {
    return apiRequest(
        `/flags/${encodeKey(key)}/overrides/${encodeKey(tenantId)}`,
        TenantFlagOverride,
        { method: 'PUT', body: input },
    );
}

/** Revert one museum to the default. Idempotent — clearing a clear override is a 204, not a 404. */
export function clearOverride(key: string, tenantId: string, reason?: string): Promise<void> {
    return apiRequestNoContent(`/flags/${encodeKey(key)}/overrides/${encodeKey(tenantId)}`, {
        method: 'DELETE',
        ...(reason !== undefined ? { body: { reason } } : {}),
    });
}
